import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Configuration,
  MemberService,
  UserRelationsService,
} from '@arkius/shared-api';
import { catchError, of, take } from 'rxjs';

@Component({
  selector: 'ark-suggested-users-modal',
  templateUrl: './suggested-users-modal.component.html',
  styleUrls: ['./suggested-users-modal.component.css'],
})
export class SuggestedUsersModalComponent implements OnInit {
  suggestedUsers: any[] = [];

  hasLoaded = false;
  page = 1;
  perPage = 10;
  nextPage = false;
  loading = false;

  constructor(
    private http: HttpClient,
    private configuration: Configuration,
    private memberService: MemberService,
    private userRelationsService: UserRelationsService,
    private modalController: ModalController,
    private router: Router,
  ) {}

  ngOnInit() {
    this.loadSuggestedUsers(1);
  }

  private loadSuggestedUsers(page: number) {
    if (this.loading) return;

    this.loading = true;
    this.fetchSuggestedUsers(page)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const users = Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response)
              ? response
              : [];
          const mappedUsers = users.map((u: any) => ({
            ...u,
            isSubscribed: !!u.isSubscribed,
          }));

          this.suggestedUsers = [
            ...(page > 1 ? this.suggestedUsers : []),
            ...mappedUsers,
          ];

          this.page = response?.pagination?.currentPage ?? page;
          this.nextPage = !!response?.pagination?.nextPage;
          this.hasLoaded = true;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching suggested users', error);
          this.hasLoaded = true;
          this.loading = false;
          this.nextPage = false;
        },
      });
  }

  private fetchSuggestedUsers(page: number) {
    const basePath = this.configuration.basePath?.replace(/\/$/, '') ?? '';
    const url = `${basePath}/api/v5/Member/discover/forYou/suggested`;
    const token = this.configuration.lookupCredential('Bearer');
    const headers = token
      ? new HttpHeaders({ Authorization: token })
      : undefined;

    return this.http.post<any>(
      url,
      {
        pageNumber: page,
        pageSize: this.perPage,
      },
      { headers },
    ).pipe(
      catchError((error) => {
        if (page === 1) {
          return this.memberService.apiV5MemberDiscoverForYouSuggestedPost();
        }

        return of({
          items: [],
          pagination: {
            currentPage: page,
            nextPage: false,
          },
        });
      }),
    );
  }

  followUser(userId: number, event: Event) {
    event.stopPropagation();
    this.userRelationsService
      .apiV5UserRelationsFollowFollowingUserIdGet(userId)
      .pipe(take(1))
      .subscribe({
        next: () => this.setSubscribed(userId, true),
        error: (error) => console.error('Error following user', error),
      });
  }

  unfollowUser(userId: number, event: Event) {
    event.stopPropagation();
    this.userRelationsService
      .apiV5UserRelationsUnfollowFollowingUserIdGet(userId)
      .pipe(take(1))
      .subscribe({
        next: () => this.setSubscribed(userId, false),
        error: (error) => console.error('Error unfollowing user', error),
      });
  }

  private setSubscribed(userId: number, value: boolean) {
    this.suggestedUsers = this.suggestedUsers.map((u) =>
      u.userId === userId ? { ...u, isSubscribed: value } : u,
    );
  }

  displayNickname(nickname: string | null | undefined) {
    if (!nickname) return '';
    return nickname.startsWith('@') ? nickname : `@${nickname}`;
  }

  loadMore() {
    if (!this.nextPage || this.loading) return;
    this.loadSuggestedUsers(this.page + 1);
  }

  goToProfile(userId: number) {
    this.modalController.dismiss();
    this.router.navigate([`/account/profile/${userId}/solutions`]);
  }

  dismiss() {
    this.modalController.dismiss();
  }
}
